import { Button, Table, Input, InputGroup } from "rsuite";
import { Link, useNavigate } from "react-router-dom";
import React, { useState, useCallback, useEffect } from "react";
import type { CellProps as RsuiteTableCellProps, SortType } from "rsuite-table";
import "./UsersTable.scss";
import DeleteConfirmationModal from "../modals/DeleteConfirmationModal/DeleteConfirmationModal";
import {AiOutlineCloseIcon, AiOutlineLockIcon, AiOutlineSearchIcon, AiOutlineUnlockIcon} from "../../models/Icons.model";
import {DevicePreview, UserRow, UsersTableProps} from "./UsersTable.model";

const { Column, HeaderCell, Cell } = Table;

export default function UsersTable({ users, loading, onDeleteUser, onToggleBlockUser }: UsersTableProps) {
  const navigate = useNavigate();
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortType, setSortType] = useState<SortType | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredUsers, setFilteredUsers] = useState<UserRow[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<UserRow | null>(null);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = users.filter((user) =>
      user.login?.toLowerCase().includes(searchLower) ||
      user.devices?.some((device) =>
        device?.clockSystemSerialNumber?.toLowerCase().includes(searchLower) ||
        device?.clockStationSerialNumber?.toLowerCase().includes(searchLower)
      ) ||
      user.role?.toLowerCase().includes(searchLower)
    );
    setFilteredUsers(filtered);
  }, [users, searchTerm]);

  const getAllClockStations = (devices?: DevicePreview[] | null): string => {
    if (!devices || devices.length === 0) return "—";

    const stations = devices.map((device) => device.clockStationSerialNumber).filter(Boolean);
    if (stations.length === 0) return "—";

    return stations.join(", ");
  };

  const getAllSerialNumbers = (devices?: DevicePreview[] | null): string => {
    if (!devices || devices.length === 0) return "—";

    const serials = devices.map((device) => device.clockSystemSerialNumber).filter(Boolean);
    if (serials.length === 0) return "—";

    return serials.join(", ");
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      onDeleteUser?.(userToDelete.id);
    }
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleDeleteClick = (user: UserRow) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const getSortedData = useCallback((): UserRow[] => {
    if (sortColumn && sortType) {
      return [...filteredUsers].sort((a, b) => {
        const getValue = (obj: UserRow, key: string): string | number => {
          if (key === "clockSystemSerialNumber") {
            return getAllClockStations(obj.devices);
          }
          if (key === "serialNumber") {
            return getAllSerialNumbers(obj.devices);
          }
          if (key === "role") {
            return obj.role || "";
          }
          if (key in obj) {
            const value = obj[key as keyof UserRow];
            return value == null ? "" : String(value);
          }
          return "";
        };

        let valueA = getValue(a, sortColumn);
        let valueB = getValue(b, sortColumn);

        const numA = parseFloat(String(valueA));
        const numB = parseFloat(String(valueB));

        if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
          valueA = numA;
          valueB = numB;
        }

        let result = 0;
        if (valueA < valueB) {
          result = -1;
        } else if (valueA > valueB) {
          result = 1;
        }

        return sortType === "asc" ? result : -result;
      });
    }
    return filteredUsers;
  }, [filteredUsers, sortColumn, sortType]);

  const handleSortColumn = (nextSortColumn: string, nextSortType?: SortType) => {
    setSortColumn(nextSortColumn);
    setSortType(nextSortType ?? null);
  };

  const handleSearch = () => {};

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const UserPreviewCell = ({ rowData, ...props }: RsuiteTableCellProps<UserRow>) => (
    <Cell {...props}>
      <div className="image-container">
        <div className="image-container__frame">
          <img
            className="image-container__img"
            src={rowData?.devices?.[0]?.previewImagePath ? getUrlImg(rowData.devices[0]) : "clock.svg"}
            alt="user-preview"
          />
        </div>
      </div>
    </Cell>
  );

  const LoginCell = ({ rowData, ...props }: RsuiteTableCellProps<UserRow>) => (
    <Cell {...props}>
      <Link to={`/users/${rowData?.id}`} onClick={(e) => e.stopPropagation()}>
        {rowData?.login}
      </Link>
    </Cell>
  );

  const RoleCell = ({ rowData, ...props }: RsuiteTableCellProps<UserRow>) => (
    <Cell {...props} className="users-table__cell-center">
      <span className="table-info-title">
        {rowData?.role || "Пользователь"}
      </span>
    </Cell>
  );

  const ActionsCell = ({ rowData, ...props }: RsuiteTableCellProps<UserRow>) => (
    <Cell {...props} className="users-table__cell-center">
      <div className="users-table__actions">
        <Button
          size="sm"
          appearance="ghost"
          color={rowData?.isBlocked ? "green" : "orange"}
          onClick={(e) => {
            e.stopPropagation();
            onToggleBlockUser?.(rowData?.id ?? "", !rowData?.isBlocked);
          }}
          title={rowData?.isBlocked ? "Разблокировать пользователя" : "Заблокировать пользователя"}
          className="users-table__action-button"
        >
          {rowData?.isBlocked ? (
            <AiOutlineUnlockIcon size={16} />
          ) : (
            <AiOutlineLockIcon size={16} />
          )}
        </Button>

        <Button
          size="sm"
          appearance="ghost"
          color="red"
          onClick={(e) => {
            e.stopPropagation();
            if (rowData) {
              handleDeleteClick(rowData);
            }
          }}
          title="Удалить пользователя"
          className="users-table__action-button"
        >
          <AiOutlineCloseIcon size={16} />
        </Button>
      </div>
    </Cell>
  );

  const getUrlImg = (device?: DevicePreview | null): string | undefined => {
    if (!device?.previewImagePath) return undefined;

    if (device.previewImagePath.startsWith("http")) {
      return device.previewImagePath;
    }

    const baseUrl = window.location.origin;
    return `${baseUrl}/${device.previewImagePath}`;
  };

  const getRowClassName = (user: UserRow) => {
    if (user?.isBlocked) {
      return "blocked-user-row";
    }
    return "";
  };

  return (
    <div>
      <div className="search-panel mb-3">
        <div className="row align-items-center">
          <div className="col-md-6 col-lg-4">
            <InputGroup>
              <Input
                placeholder="Поиск пользователей..."
                value={searchTerm}
                onChange={setSearchTerm}
                onPressEnter={handleSearch}
              />

              {!searchTerm && (
                <InputGroup.Button onClick={handleSearch}>
                  <AiOutlineSearchIcon />
                </InputGroup.Button>
              )}

              {searchTerm && (
                <InputGroup.Button onClick={handleClearSearch}>
                  ×
                </InputGroup.Button>
              )}
            </InputGroup>
          </div>
          <div className="col-md-6 col-lg-8 text-md-end">
            <span className="text-muted">
              Найдено: {filteredUsers.length} из {users.length} пользователей
            </span>
          </div>
        </div>
      </div>

      <Table
        height={450}
        data={getSortedData()}
        sortColumn={sortColumn ?? undefined}
        sortType={sortType ?? undefined}
        onSortColumn={handleSortColumn}
        loading={loading}
        onRowClick={(rowData) => navigate(`/users/${rowData.id}`)}
        className="users-table"
        rowHeight={95}
        rowClassName={getRowClassName}
      >
        <Column flexGrow={1} sortable verticalAlign="middle">
          <HeaderCell>Логин</HeaderCell>
          <LoginCell />
        </Column>

        <Column width={210} sortable verticalAlign="middle" align="center">
          <HeaderCell className="users-table__cell-center">Телефон</HeaderCell>
          <Cell className="users-table__cell-center" dataKey="phone">
            {(rowData: UserRow) => (
              <span className="table-info-title">
                {rowData?.phone}
              </span>
            )}
          </Cell>
        </Column>

        <Column width={150} fullText>
          <HeaderCell>Превью</HeaderCell>
          <UserPreviewCell />
        </Column>

        <Column width={250} sortable verticalAlign="middle" align="center">
          <HeaderCell className="users-table__cell-center">Часовая станция</HeaderCell>
          <Cell className="users-table__cell-center" dataKey="clockSystemSerialNumber">
            {(rowData: UserRow) => (
              <span className="table-info-title">
                {getAllClockStations(rowData?.devices)}
              </span>
            )}
          </Cell>
        </Column>

        <Column width={250} sortable verticalAlign="middle" align="center">
          <HeaderCell className="users-table__cell-center">Серийный номер системы</HeaderCell>
          <Cell className="users-table__cell-center" dataKey="serialNumber">
            {(rowData: UserRow) => (
              <span className="table-info-title">
                {getAllSerialNumbers(rowData?.devices)}
              </span>
            )}
          </Cell>
        </Column>

        <Column width={150} sortable verticalAlign="middle" align="center">
          <HeaderCell className="users-table__cell-center">Права</HeaderCell>
          <RoleCell dataKey="role" />
        </Column>

        <Column width={120} verticalAlign="middle" align="center">
          <HeaderCell className="users-table__cell-center">Действия</HeaderCell>
          <ActionsCell />
        </Column>
      </Table>

      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        itemName={`пользователя ${userToDelete?.login || ""}`}
      />
    </div>
  );
}
